/**
 * Begin Strength - Release Notes Data
 * 
 * This file contains structured release notes data for programmatic access
 * and can be used for displaying release notes in the application.
 * 
 * Format for all releases:
 * - newFeatures: New functionality and capabilities
 * - bugFixes: Issues resolved
 * - optimizations: Performance improvements and enhancements
 */

// Current app version info
export const currentVersion = '1.1.1';
export const currentReleaseDate = '2026-10-03';

// Marketing site releases (separate from app versioning)
export const siteReleases = [
  {
    title: 'Club document security & Firestore read efficiency',
    releaseDate: '2026-10-03',
    newFeatures: [
      {
        category: 'Club billing & tax documents',
        items: [
          'W-9 uploads now go to a private Storage path (`club-private/{clubId}/…`) — not world-readable like logo/banner assets',
          'Club Manage stores the private path on the club record; Admin Clubs still opens W-9s via authenticated download URLs',
        ]
      }
    ],
    bugFixes: [
      'Closed a data-leak vector: tax documents (SSN/EIN) must no longer sit under the public `clubs/{clubId}/` prefix',
      'Club branding uploads restricted to images and to the verified club owner (not any signed-in user)',
    ],
    optimizations: [
      'Dashboard week view uses one ranged Firestore query plus parallel activityLog reads instead of seven separate day queries',
      'Workout player loads warmup and finisher templates by id (single-doc reads) instead of pulling full template catalogs',
      'Admin Content loads exercises, warmups, finishers, drills, and workouts only when their tab is active — fewer accidental full-collection reads',
      'Scheduled-activities-for-day fetches activity log in parallel with the day query',
    ]
  },
  {
    title: 'Functional Beginner Year-One Programming',
    releaseDate: '2026-07-29',
    newFeatures: [
      {
        category: 'Functional Strength — Beginner',
        items: [
          'Full-year main blocks (Q1–Q4, days A–F) rebuilt for Begin Gym — barbell squats/bench/press, pull-up bar work, and arms on push/pull days without loop bands',
          'Volume ladder by quarter — 3 circuits at 40 min (Q1), 4 at 50 min (Q2), 5–6 at 60 min (Q3–Q4); warmups and finishers stay on their existing templates',
          'Programming rules documented in-repo for disjoint A/D, B/E, and C/F days and tri-domain full-body circuits',
        ]
      },
      {
        category: 'Content tooling',
        items: [
          'Reviewed apply scripts write main blocks only to Firestore — warmup and finisher assignments are preserved on each template',
          'Begin Gym exercise validator for programming QA before CMS writes',
        ]
      }
    ],
    bugFixes: [],
    optimizations: [
      'Functional Beginner program equipment updated in Firestore (barbell, bench, squat cage, dumbbells, kettlebells)',
    ]
  },
  {
    title: 'Clubs, Content Engine & Boathouse Pro Forma',
    releaseDate: '2026-07-25',
    newFeatures: [
      {
        category: 'Club Management',
        items: [
          'Club Manage page — club owners can edit club details, upload a logo, and manage teams from one place',
          'Age-group teams & schedules — create teams by age group and manage each team\'s practice schedule',
          'Club Reports — membership and activity analytics with trend charts broken down by age group',
          'Admin Clubs console — platform admins can view, filter, and manage every club',
          'Club navigation — quick access to Manage and Reports from the dashboard sidebar and More panel',
        ]
      },
      {
        category: 'Training Content CMS',
        items: [
          'The CMS is now the single source of truth — programs, workouts, warmups, finishers, exercises, and equipment are authored in the admin and stored in the database',
          'Program dropdowns are dynamic — any program you publish now appears automatically in the Warmups, Finishers, and Workouts filters and editors',
          'Workout player reads warmups and finishers from the database, so content edits reach athletes immediately',
          'Mesocycle slot validation — the program editor warns when weekly workout slots (A–F) are incomplete, preventing repeated or misaligned workouts',
        ]
      },
      {
        category: 'Boathouse District Pro Forma',
        items: [
          'Interactive OKC Fieldhouse pro forma at /pro-forma — model buy vs. lease scenarios, revenue mix, projected performance, and economic impact with editable assumptions',
        ]
      }
    ],
    bugFixes: [
      'Published programs now appear in the Content program dropdowns instead of a fixed list',
      'Fixed repeated warmups and near-identical workouts caused by incomplete mesocycle slot arrays',
      'Fixed the Workouts editor showing blank warmup, finisher, and exercise dropdowns — it now loads every option from the database, so any workout opens with its warmup, exercises, and finisher pre-selected',
    ],
    optimizations: [
      'Retired all JSON-to-Firestore sync scripts and the sync Cloud Function — training content now flows one way from the CMS',
      'Added `npm run backup:content` to export database content back to versioned JSON as a backup / audit trail',
      'Added getWarmupTemplate / getFinisherTemplate database helpers',
      'Added admin-triggered demo data seeding for clubs',
      'Updated Firestore and Storage security rules for clubs and demo data',
    ]
  },
  {
    title: 'Coach & Team Management',
    releaseDate: '2026-02-21',
    newFeatures: [
      {
        category: 'Coach System',
        items: [
          'Coach signup flow — coaches can create an account and set up their profile',
          'Coach dashboard — manage athletes, teams, and training programs',
          'Coach landing page — public-facing page for coaches to share with athletes',
          'Coach settings section — configure coaching preferences and team options',
        ]
      },
      {
        category: 'Team Management',
        items: [
          'Team schedule editor — build and manage weekly practice schedules for teams',
          'Team switch confirmation — safely switch between teams with data protection',
          'Team support in onboarding — athletes can join a coach\'s team during signup',
          'Team features on dashboard — see team info, schedule, and coach details',
        ]
      },
      {
        category: 'Program Switching',
        items: [
          'Change program page — browse and switch training programs without re-onboarding',
          'Program selector component — reusable program picker for onboarding and settings',
        ]
      },
      {
        category: 'Admin Expansion',
        items: [
          'Program form overhaul — expanded CMS for macrocycles, mesocycles, and scheduling',
          'Admin docs page — in-app API documentation for developers',
          'Admin settings — additional configuration options for platform management',
        ]
      },
      {
        category: 'UI & Styling',
        items: [
          'Settings page refactor — cleaner layout with coach and team sections',
          'Onboarding flow updates — improved steps for club and coach-based signups',
          'Club signup enhancements — coach and team selection during club registration',
          'Install prompt redesign — updated styling for PWA install banner',
          'Theme context improvements — better dark/light mode handling',
          'Style updates across Dashboard, Auth, Onboarding, BuildingPlanScreen, and Settings',
        ]
      }
    ],
    bugFixes: [
      'Fixed Firestore rules for coach and team data access',
      'Fixed theme context edge cases with system preference detection',
    ],
    optimizations: [
      'Expanded database utilities with coach, team, and program management APIs',
      'Added coach utility functions for data fetching and team operations',
      'Added API documentation in docs/API.md',
      'Updated Firestore security rules for new coach and team collections',
    ]
  },
  {
    title: 'Web Platform Update',
    releaseDate: '2026-02-17',
    newFeatures: [
      {
        category: 'Volleyball Clubs',
        items: [
          'Club landing pages — public-facing pages for volleyball club programs',
          'Club signup flow — players can join a club and select age group',
          'Club dashboard — club owners can view players and manage settings',
          'Club settings — configure practice schedule, markup pricing, and landing page',
          'Age group support — age-based player grouping for youth programs',
        ]
      },
      {
        category: 'Admin CMS Overhaul',
        items: [
          'Workout editor modal — build and edit workout templates with warmup, main, and finisher blocks',
          'Reconcile modal — compare local JSON data against Firestore and sync differences',
          'Admin navigation redesign with sidebar and section-level nav',
          'Program form — full CMS for macrocycles, mesocycles, weekly calendars, and workout assignment',
          'Drill templates — new content type for sport-specific power and agility drills',
        ]
      },
      {
        category: 'Database-First Content',
        items: [
          'Exercises now read and write exclusively from Firestore — no more JSON fallback',
          'Admin CMS edits persist immediately in the database',
          'Workout page pulls exercise data (descriptions, equipment, categories) from Firestore',
          'New admin data layer for all content types — equipment, exercises, warmups, finishers, drills, workout templates',
        ]
      },
      {
        category: 'Program System',
        items: [
          'Programs config — centralized program metadata and routing',
          'Program build flow — macro → meso → microcycle generation pipeline',
          'Program API contract — documented REST endpoints for content, user, and workout services',
          'Assign workouts to programs script — bulk-link workout templates to program catalogs',
          'Import programs from mobile — migrate program data from mobile app format',
        ]
      },
      {
        category: 'Security',
        items: [
          'Admin-only Firestore rules — content collection writes locked to admin email',
          'All authenticated users can still read content; equipment remains public-read',
          'Firestore indexes updated for new collections and queries',
        ]
      },
      {
        category: 'Pages & UI',
        items: [
          'Renew page for subscription renewals',
          'Updated Dashboard, Equipment, Home, Programs, Settings, and Workout pages',
          'Auth and onboarding flow improvements',
          'Design token and component style updates across the app',
        ]
      }
    ],
    bugFixes: [
      'Fixed exercises appearing in CMS that were not in the database (JSON fallback masking missing DB data)',
      'Fixed exercise updates not persisting (CMS reading stale JSON instead of Firestore)',
      'Fixed admin layout auth flow and login modal overlay',
    ],
    optimizations: [
      'Archived 30+ legacy docs into docs/archive/ for cleaner project structure',
      'Added new documentation — API contract, program API, program build flow, club owner signup plan, admin DB mapping',
      'Cleaned up all ESLint warnings for a zero-warning build',
      'Removed unused imports and variables across AdminProgramForm, AdminContent, ClubSettingsSection, and ClubDashboard',
      'Added test fixtures and test scaffolding',
    ]
  },
  {
    title: 'Marketing Site & CMS',
    releaseDate: '2026-02-12',
    newFeatures: [
      {
        category: 'Program Microservices & API',
        items: [
          'Microservices architecture - Designed Content, User, and Workout services behind a shared API gateway for PWA and mobile apps',
          'Program API feed - 16 programs synced to Firestore (catalog → programs/{id}, runtime → programTemplates/{templateId})',
          'Cloud Function sync - Admin-only callable function (syncProgramsToFirestore) pushes program JSON from server to Firestore in one call',
          'Local sync script - scripts/syncProgramsToFirestore.js for CLI-based program deploys',
          'Shared sync logic - functions/syncProgramsLogic.js shared between Cloud Function and local script',
          'API endpoint spec - Full REST API defined: /v1/content/, /v1/users/, /v1/workouts/, /v1/admin/',
          'Auth middleware - Bearer token verification via Firebase Admin SDK',
          'Personalized workouts endpoint - GET /v1/workouts/today returns workout with user baselines and week modifiers applied',
          'API client for PWA and mobile - Reusable ApiClient class with contentApi, userApi, and workoutApi helpers',
          'Program DB architecture - Catalog vs runtime template separation documented and implemented'
        ]
      },
      {
        category: 'Equipment & Admin',
        items: [
          'Equipment page - Browse all facility equipment organized by tier with filtering',
          'Admin panel - CMS for managing programs, equipment, and content',
          'Program sync button - Push program updates from admin UI to Firestore in one click',
          'Equipment tiers - Organized equipment into Pod, Base, and Full tiers'
        ]
      },
      {
        category: 'Apparel Drops',
        items: [
          'Email signup fix - Drops alert signup now works (added Firestore security rule)',
          'Product image modal - Fullscreen lightbox with thumbnail strip, image counter, and smooth animations',
          'Keyboard and swipe navigation - Use arrow keys or swipe to browse product photos',
          'Custom size dropdown - Matches site design with hover states',
          'Pre-order validation - Error message when clicking Pre-Order without picking a size',
          'Hover zoom effect on product cards'
        ]
      },
      {
        category: 'Font & Performance',
        items: [
          'Fixed font flash on refresh - Fonts no longer briefly show fallback before loading',
          'Added missing Poppins weight 300 to Google Fonts',
          'Preconnect hints for faster font loading'
        ]
      }
    ],
    bugFixes: [
      'Fixed Firestore dropsSignups rule - email signup was failing silently',
      'Fixed font-display causing visible font swap on page load',
      'Removed duplicate Google Fonts @import in CSS'
    ],
    optimizations: [
      'Firestore rules updated for equipment, programs, and public collections',
      'Storage rules updated for profile pictures and assets',
      'Cloud Functions updated with program sync and SSR improvements',
      '16 program JSON files deployed to functions/data/programs/ for server-side sync',
      'Admin-only access enforced on sync endpoint via email check'
    ]
  }
];

// App releases (newest first)
export const releases = [
  {
    version: '1.0.9',
    releaseDate: '2026-01-29',
    newFeatures: [
      {
        category: 'Apple Health Integration',
        items: [
          'Import activities from Apple Health - Walks, runs, swims, and other workouts recorded on your Apple Watch or other apps now automatically appear on your Dashboard',
          'Better activity tracking - Imported activities show accurate duration (including seconds) and distance in your preferred units (miles or km)',
          'HealthKit Sync page improvements - View your imported and exported activities with easy pagination'
        ]
      },
      {
        category: 'Calendar Improvements',
        items: [
          'See all your workouts - Bonus workouts and activities imported from Apple Health now appear on your Calendar',
          'Visual distinction - Program activities appear in orange, while manually added or imported activities appear in grey'
        ]
      }
    ],
    bugFixes: [
      'Fixed time display showing AM instead of PM when editing activities',
      'Fixed distance not showing correctly for imported activities',
      'Improved activity data accuracy throughout the app'
    ],
    optimizations: []
  },
  {
    version: '1.0.8',
    releaseDate: '2026-01-01',
    newFeatures: [
      {
        category: 'General',
        items: [
          'Initial HealthKit export support',
          'Workout tracking improvements',
          'Performance enhancements'
        ]
      }
    ],
    bugFixes: [],
    optimizations: []
  },
  {
    version: '1.0.7',
    releaseDate: '2026-01-01',
    newFeatures: [],
    bugFixes: ['Bug fixes and stability improvements'],
    optimizations: []
  },
  {
    version: '1.3.0',
    releaseDate: '2026-01-11',
    newFeatures: [
      {
        category: 'Light & Dark Mode',
        items: [
          'Choose your look - Switch between Light, Dark, or System (follows your device settings)',
          'Find it in Settings - New Appearance option in the General tab',
          'Syncs across devices - Your theme preference saves to your account'
        ]
      },
      {
        category: 'Dashboard Improvements',
        items: [
          'Better program card layout - Macrocycle and Mesocycle cards now have equal heights',
          'Cleaner visualization - Macrocycle progress bars display below the title',
          'More room for activities - Cards align perfectly with improved spacing'
        ]
      },
      {
        category: 'Mobile & Tablet Updates',
        items: [
          'Bigger streak boxes on Awards - Easier to see your progress at a glance',
          'Larger heat map days - 24px on mobile, 40px on desktop',
          'Square metric cards - Streak, Duration, and Distance boxes are now perfectly square'
        ]
      },
      {
        category: 'Settings Redesign',
        items: [
          'Tab navigation - Settings now organized into tabs: Profile, General, Goals, Pace, Integrations',
          'Less scrolling - Jump directly to the section you need',
          'Cleaner layout - Each section has its own dedicated space'
        ]
      }
    ],
    bugFixes: [
      'Fixed white text on red backgrounds in light mode',
      'Fixed chart gradients not matching container backgrounds in light mode',
      'Fixed dropdown arrow positioning - now has proper padding',
      'Fixed Program page text visibility in light mode',
      'Fixed Calendar day cards text color on red backgrounds'
    ],
    optimizations: [
      'Refined light mode color palette with optimized greys for buttons, borders, and text',
      'Sidebar and bottom navigation stay consistent in both modes',
      'Activity cards have more breathing room with increased height on mobile',
      'All titles on Program page are now left-aligned for consistency',
      'Performance charts adapt smoothly between light and dark themes'
    ]
  },
  {
    version: '1.0.2',
    releaseDate: '2026-01-07',
    newFeatures: [
      {
        category: 'Awards',
        items: [
          'Redesigned Streaks - Full-width 90-day activity visualization',
          'Live Streak Stats - Current streak, longest streak, and active days count'
        ]
      },
      {
        category: 'Calendar',
        items: [
          'Program Progress - See your Macrocycle timeline and Mesocycle progress below the calendar',
          'Day Details Modal - Tap any day to see all scheduled activities in a clean overlay',
          'Quick Add Activity - Add activities with optional duration, distance, and time of day'
        ]
      },
      {
        category: 'Workouts',
        items: [
          'Tailored Warmups - Each workout now includes a warmup matched to the day\'s focus (upper, lower, or full body)',
          'Smart Start Workout - Bottom nav button opens today\'s workout or your next scheduled session',
          'Smooth Transitions - Workout page slides in and out for a polished mobile experience'
        ]
      },
      {
        category: 'Activity Tracking',
        items: [
          'Edit Activities - Tap the edit icon on any activity to update duration, distance, or time',
          'Detailed Metadata - See time, duration, and distance right on your activity cards',
          'Live Performance Updates - Charts update instantly when you complete or edit activities'
        ]
      },
      {
        category: 'Performance',
        items: [
          'Redesigned Performance Page - Full-width charts for distance and duration',
          'Date Tooltips - Hover on any data point to see the exact date (M/D format)',
          'Summary Stats - Total and daily averages displayed below each chart'
        ]
      }
    ],
    bugFixes: [
      'Workout inputs - Tap to clear and type new values, numeric keyboard on mobile',
      'Improved PWA login - Better password autofill and Face ID support',
      'Fixed mobile header layout - logo left, menu right'
    ],
    optimizations: [
      'Enhanced warmup volume - all warmup exercises now include 2 sets for proper activation',
      'Unified activity modals across Dashboard and Calendar',
      'Cleaner activity card layout with edit functionality',
      'Faster number entry - tap any duration, distance, or time field to instantly replace the value'
    ]
  },
  {
    version: '1.0.1',
    releaseDate: '2026-01-02',
    newFeatures: [
      {
        category: 'Public Website',
        items: [
          'Home Page - Discover what makes Begin Strength unique',
          'About Us - Learn our mission, story, and meet the founding coaches',
          'Facility Tour - See concept renders and learn about our training areas',
          'Plans & Pricing - Find the membership option that fits your needs',
          'FAQs - Get answers to common questions',
          'For Coaches - Learn about our co-working space',
          'Periodization Guide - Interactive visualization explaining training phases',
          'SEO & Social Sharing - Page-specific Open Graph images and descriptions for better social media previews',
          'Programs Page - Updated hero and feature sections with app screenshots',
          'Mobile-First Design - Full mobile responsiveness across all marketing pages'
        ]
      },
      {
        category: 'Authentication & Onboarding',
        items: [
          'Easy Sign Up - Create your account in seconds',
          'Site Navigation on Auth Pages - Full header navigation on sign in/sign up pages',
          'Personalized Onboarding - Tell us about your goals and we\'ll set up your perfect program',
          'Mobile-Optimized Onboarding - Touch-friendly image cropping, stacked inputs, edge-to-edge layouts',
          'Coming Soon Badges - Visual indicators for programs in development',
          'Mobile Tooltips - Centered modal tooltips for better mobile UX',
          'Your Profile - Customize your preferences, goals, and training settings'
        ]
      },
      {
        category: 'Dashboard',
        items: [
          'Personalized Dashboard - Start each day with a motivational quote',
          'This Week\'s Activities - Quick 7-day view',
          'Training Progress - Visual progress bars',
          'Last 30 Days Performance - Beautiful charts',
          'Smart Activity Display - Activities shown as simple categories'
        ]
      },
      {
        category: 'Calendar',
        items: [
          'Monthly View - See your entire month of training',
          'Schedule Your Activities - Plan when you\'ll train',
          'Log Your Workouts - Mark activities complete',
          'Visual Feedback - Color-coded activities'
        ]
      },
      {
        category: 'Training Programs',
        items: [
          'Smart Program Selection - Tell us your goals',
          'Multi-Sport Athlete Program - Year-round training',
          'Volleyball Programs - Specialized training',
          'Automatic Adjustments - Program adapts to the season'
        ]
      },
      {
        category: 'Activity Tracking',
        items: [
          'Track Everything You Do - Log runs, swims, bike rides, strength sessions',
          'Add Custom Activities - Add your own activities',
          'Quick Logging - Mark activities complete with one tap',
          'Your Training History - See everything you\'ve done'
        ]
      },
      {
        category: 'Strength Workouts',
        items: [
          'Follow Your Program - Access scheduled strength workouts',
          'Log Your Sets - Track sets, reps, and weight',
          'Built-in Workouts - Complete workout templates',
          'Exercise Library - 596 exercises with detailed instructions'
        ]
      },
      {
        category: 'Performance Analytics',
        items: [
          'Performance Dashboard - Beautiful charts',
          'Monthly Summary - Duration, distance, and activity breakdowns',
          'Consistency Tracking - Visual heat map',
          'Progress Over Time - Time-series charts',
          'Set Goals - Weekly or monthly goals'
        ]
      },
      {
        category: 'Awards & Achievements',
        items: [
          'Daily Streaks - Consecutive days of training',
          'Monthly Consistency - Track consistency month over month',
          'Achievements & Badges - Unlock achievements',
          'Recognition - Get notified for milestones'
        ]
      }
    ],
    bugFixes: [
      'Fixed manual activities disappearing from Microcycle Activity component',
      'Fixed macrocycle and mesocycle progress calculation',
      'Fixed activity name display (blank titles issue)',
      'Fixed duplicate activity display',
      'Fixed time format conversion (24-hour to 12-hour)',
      'Fixed activity categorization for endurance activities',
      'Fixed Performance Tracker layout and height matching',
      'Fixed white background flash when navigating between pages',
      'Fixed hamburger menu X icon display issue',
      'Fixed header shift when toggling hamburger menu',
      'Fixed logo position shift on mobile navigation',
      'Fixed hero header sizing and red word placement across all pages'
    ],
    optimizations: [
      'Improved activity name shortening and display',
      'Enhanced activity categorization logic',
      'Improved macrocycle visualization with daily progress lines',
      'Better time format handling and conversion',
      'Improved error handling and logging',
      'Enhanced data validation',
      'Better code organization and structure',
      'Removed hardcoded program templates in favor of JSON-based system',
      'Client-side navigation for smoother page transitions (Link components)',
      'Inline background colors to prevent flash of unstyled content',
      'Touch event support for mobile image cropping',
      'Responsive breakpoints for small mobile devices (375px)'
    ]
  }
];

// Legacy export for backwards compatibility
export const releaseNotes = {
  version: currentVersion,
  releaseDate: currentReleaseDate,
  isInitialRelease: false,
  ...releases[0],
  
  statistics: {
    exercises: 596,
    equipmentTypes: ['barbell', 'dumbbell', 'kettlebell', 'bands', 'med_ball', 'plyobox', 'pullup_bar', 'stability_ball'],
    trainingPersonas: ['multi_sport_training', 'court_sports', 'beginner'],
    seasons: ['winter', 'spring', 'summer', 'fall'],
    programTypes: ['multi_sport', 'volleyball', 'beginner'],
    description: '596 exercises, multiple equipment options, 3 training personas, 4 seasons of programming, and complete programs with workout templates, warmups, and finishers included'
  },
  
  comingSoon: [
    'Enhanced workout tracking with real-time exercise logging',
    'Social features and coach-athlete connections',
    'Mobile app (iOS/Android)',
    'Advanced analytics and insights',
    'Export functionality for workouts and programs',
    'Integration with fitness wearables'
  ]
};

export default releaseNotes;
